import React, { useState, useRef } from 'react';
import { Upload, X, FileText, Image as ImageIcon, File, Loader2, Check } from 'lucide-react';
import axios from 'axios';
import { getAuthHeaders } from '../utils/auth';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FileUpload = ({ onFileUploaded, compact = false }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    for (const file of files) {
      await uploadFile(file);
    }

    // Clear input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadFile = async (file) => {
    // Validate file type
    const allowedTypes = [
      'image/png',
      'image/jpeg',
      'image/jpg',
      'image/webp',
      'application/pdf',
      'text/plain',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];

    if (!allowedTypes.includes(file.type)) {
      toast.error(`${file.name}: Unsupported file type. Allowed: PNG, JPG, WEBP, PDF, TXT, DOCX`);
      return;
    }

    // Validate file size (20MB)
    const maxSize = 20 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`${file.name}: File too large. Max size: 20MB`);
      return;
    }

    try {
      setUploading(true);

      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/files/upload`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          console.log(`Upload progress: ${percentCompleted}%`);
        }
      });

      const uploadedFile = {
        ...response.data,
        name: file.name,
        type: file.type,
        size: file.size
      };

      setUploadedFiles(prev => [...prev, uploadedFile]);
      
      if (onFileUploaded) {
        onFileUploaded(uploadedFile);
      }

      toast.success(`${file.name} uploaded successfully!`);
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(`Failed to upload ${file.name}: ${error.response?.data?.detail || error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const removeFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(f => f.file_id !== fileId));
  };

  const getFileIcon = (type) => {
    if (type.startsWith('image/')) return <ImageIcon className="w-4 h-4" />;
    if (type === 'application/pdf') return <FileText className="w-4 h-4" />;
    return <File className="w-4 h-4" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (compact) {
    return (
      <div className="relative">
        {/* iOS-compatible file input - overlays the button */}
        <label className="relative inline-block cursor-pointer">
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
            style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '100%' }}
            multiple
            accept="image/*,.pdf,.txt,.docx"
            capture="environment"
            disabled={uploading}
          />
          <div className={`p-2 hover:bg-secondary/50 rounded-lg transition-colors ${uploading ? 'opacity-50' : ''}`} title="Upload file" data-testid="upload-file-button">
            {uploading ? (
              <Loader2 className="w-5 h-5 animate-spin text-primary" />
            ) : (
              <Upload className="w-5 h-5 text-muted-foreground" />
            )}
          </div>
        </label>

        {/* Uploaded files mini preview */}
        {uploadedFiles.length > 0 && (
          <div className="absolute bottom-full mb-2 left-0 bg-secondary border border-border rounded-lg p-2 shadow-lg min-w-[200px]">
            <div className="text-xs text-muted-foreground mb-2">
              {uploadedFiles.length} file{uploadedFiles.length > 1 ? 's' : ''} attached
            </div>
            {uploadedFiles.map((file) => (
              <div
                key={file.file_id}
                className="flex items-center gap-2 text-xs bg-background/50 rounded p-1 mb-1"
              >
                {getFileIcon(file.type)}
                <span className="flex-1 truncate">{file.name}</span>
                <button
                  onClick={() => removeFile(file.file_id)}
                  className="hover:text-destructive"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* iOS-compatible file input with label overlay */}
      <label className="block cursor-pointer">
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileSelect}
          className="hidden"
          multiple
          accept="image/*,.pdf,.txt,.docx"
          capture="environment"
          disabled={uploading}
        />
        <div className={`w-full border-2 border-dashed border-border hover:border-primary rounded-lg p-8 transition-all ${uploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`} data-testid="upload-area">
          <div className="flex flex-col items-center gap-3">
            {uploading ? (
              <>
                <Loader2 className="w-10 h-10 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground">Uploading...</p>
              </>
            ) : (
              <>
                <Upload className="w-10 h-10 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Tap to upload files</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    PNG, JPG, WEBP, PDF, TXT, DOCX (max 20MB)
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </label>

      {/* Uploaded files list */}
      {uploadedFiles.length > 0 && (
        <div className="space-y-2">
          <div className="text-sm font-medium">
            Uploaded Files ({uploadedFiles.length})
          </div>
          {uploadedFiles.map((file) => (
            <div
              key={file.file_id}
              className="flex items-center gap-3 p-3 bg-secondary rounded-lg border border-border"
            >
              <div className="p-2 bg-primary/10 rounded">
                {getFileIcon(file.type)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{file.name}</div>
                <div className="text-xs text-muted-foreground">
                  {formatFileSize(file.size)} • {file.type.split('/')[1].toUpperCase()}
                </div>
              </div>
              <Check className="w-5 h-5 text-green-500" />
              <button
                onClick={() => removeFile(file.file_id)}
                className="p-1 hover:bg-destructive/10 rounded hover:text-destructive transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FileUpload;
